import {
  cloneElement,
  CSSProperties,
  ReactElement,
  ReactNode,
  useContext,
  useState,
  useEffect
} from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/router';
import { Dropdown } from 'antd';
import { UserOutlined } from '@ant-design/icons';

import { UserInfoDisplay } from '@components/layout/Header/SettingsDisplay/UserInfoDisplay';
import { parseFavoriteUsers } from '@features/superadmin/users/utils/parseFavoriteUsers';
import { UserContext } from '@providers/UserProvider/UserProvider';

import { handleOptionClick } from './handleOptionClick';
import { getDropdownOptions } from './getDropdownOptions';
import style from './UserDropdown.module.scss';

// TODO: we need to adjust ADS to resolve the import issue `Cannot use namespace 'MenuProps' as a type.`
import type { MenuProps } from 'antd';

/**
 * LaunchDarkly Flags: enableNewSettingsPage
 */
export const UserDropdown = () => {
  const router = useRouter();
  const { t } = useTranslation('common');
  const { enableNewSettingsPage = false } = useFlags();
  const user = useContext(UserContext);
  const [favoriteUsers, setFavoriteUsers] = useState({});
  const [userAvatarUrl, setUserAvatarUrl] = useState(undefined);
  const avatarSize = 40;

  useEffect(() => {
    setFavoriteUsers(parseFavoriteUsers(window.localStorage.getItem('FavoriteUsers') || ''));
  }, []);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== 'FavoriteUsers') return;
      // null newValue means localStorage.removeItem was called (e.g. logout); ignore it so we don't wipe the list
      if (e.newValue === null) return;
      setFavoriteUsers(parseFavoriteUsers(e.newValue));
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    const {
      profile_picture_url: userProfilePictureUrl,
    } = user;

    setUserAvatarUrl(userProfilePictureUrl);
  }, [user]);

  const menuStyle: CSSProperties = {
    boxShadow: 'none',
  };

  const handleClick: MenuProps['onClick'] = ({ key }) => {
    return handleOptionClick({ key, router, t, user });
  };

  const customDropdownRender = (menu: ReactNode) => {
    return (
      <div className={style.dropdownMenuContainer} data-testid='avatar-profile-settings-dropdown-menu'>
        <div className={style.userDisplayMenu}>
          <UserInfoDisplay isInDropdown />
        </div>
        {menu && cloneElement(
          menu as ReactElement<{
            style: CSSProperties;
          }>,
          { style: menuStyle },
        )}
      </div>
    );
  };

  const items: MenuProps['items'] = getDropdownOptions({
    enableNewSettingsPage,
    favoriteUsers,
    t,
    user
  });

  return (
    <div className={style.dropdownWrapper}>
      <Dropdown
        menu={{ items, onClick: handleClick }}
        placement='bottomRight'
        popupRender={(menu: ReactNode) => customDropdownRender(menu)}
        trigger={['click']}
      >
        <div data-testid='avatar-profile-settings-dropdown'>
          {userAvatarUrl ? (
            <img
              alt={t('general.your_user_avatar')}
              data-testid='profile-avatar'
              height={avatarSize}
              src={userAvatarUrl}
              style={{ borderRadius: 50 }}
              width={avatarSize}
            />
          ) : (
            <UserOutlined
              data-testid='profile-default-avatar'
              style={{ fontSize: avatarSize }}
            />
          )}
        </div>
      </Dropdown>
    </div>
  );
};
